import React, { ReactElement, useEffect, useState } from "react";
import { Theme } from "../../styles/theme";
import { getSystemPreference, readVisitorThemePreference, setVisitorThemePreference, VISITOR_THEME_STORAGE_KEY } from "../../styles/visitorThemePreference";

// Re-exported for backwards compatibility — these used to be defined
// locally in this file. The canonical implementations now live in
// `common/styles/visitorThemePreference.ts`, shared with
// `useActiveBrandTheme` (see that file), so both read/write the exact same
// stored value/key without duplicating the logic.
export { VISITOR_THEME_STORAGE_KEY, setVisitorThemePreference };
type BrandStyleTagProps = {
  theme: Theme;
  /**
   * When provided alongside `darkTheme`, BrandStyleTag renders whichever of
   * `theme`/`darkTheme` matches the currently-active resolved theme bucket
   * (`activeTheme`), reacting live to OS-level `prefers-color-scheme`
   * changes (when `brandThemeMode` is "auto" and no visitor override is
   * stored) and to the visitor's own manually-stored theme preference (see
   * `VISITOR_THEME_STORAGE_KEY` below). When omitted, behaves exactly as
   * before — statically renders `theme`.
   */
  darkTheme?: Theme;
  /**
   * Whether the visitor is allowed to manually override the resolved
   * theme (mirrors `Brand.allowVisitorThemeOverride`). Only relevant when
   * `darkTheme` is provided.
   */
  allowVisitorThemeOverride?: boolean;
};

/**
 * Bridges the styled-components ThemeProvider (which does not itself emit
 * real CSS custom properties) with plain CSS custom properties, by
 * injecting the resolved `--d-*` brand tokens (see common/styles/theme.ts)
 * onto `:root`. This lets Tailwind utility classes registered in
 * styles/tailwind.css (bg-b-surface, text-b-muted, border-b-border,
 * font-b-heading, etc.) pick up brand-customised values.
 *
 * Also conditionally loads a client-supplied web font stylesheet
 * (brand.fontUrl) if present, and — when `darkTheme` is supplied — sets a
 * `data-theme="light"|"dark"` attribute on `<html>` (for downstream
 * CSS/Tailwind dark-mode variants to key off) reflecting the currently
 * active bucket, reacting live to OS `prefers-color-scheme` changes when no
 * visitor override is stored.
 */
export function BrandStyleTag({
  theme,
  darkTheme,
  allowVisitorThemeOverride = true
}: BrandStyleTagProps): ReactElement {
  const [systemPreference, setSystemPreference] = useState<"light" | "dark" | null>(() => getSystemPreference());
  const [visitorOverride] = useState<"light" | "dark" | null>(() => allowVisitorThemeOverride ? readVisitorThemePreference() : null);
  useEffect(() => {
    if (!darkTheme || typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (event: MediaQueryListEvent) => {
      setSystemPreference(event.matches ? "dark" : "light");
    };
    // Safari < 14 only supports addListener/removeListener; both are
    // supported here for broad compatibility.
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, [darkTheme]);
  const activeTheme = darkTheme ? (visitorOverride ?? systemPreference ?? "light") === "dark" ? darkTheme : theme : theme;
  const {
    cssVars,
    fontUrl
  } = activeTheme.brand;
  const declarations = Object.entries(cssVars).map(([name, value]) => `  ${name}: ${value};`).join("\n");
  return <>
      {fontUrl && <link rel="stylesheet" href={fontUrl} />}
      <style>{`:root {\n${declarations}\n}\nhtml {\n  color-scheme: ${activeTheme.brand.theme};\n}`}</style>
    </>;
}
export default BrandStyleTag;